import React from "react";
import { Column } from "../components/Layout";
import { Signature } from "../components/Signature";
import { FadeIn } from "../components/FadeIn";
import { Seo } from "../components/Seo";
import { OutlineLink } from "../components/OutlineLink";

const DELIVERABLES = [
  {
    title: "The Text",
    body: "A full literary volume, built on a thematic architecture found in the life itself rather than a flat chronology. Every life carries its own structure; the work of the fieldwork is to find it. It is written to be read as literature, not consulted as a record.",
  },
  {
    title: "The Images",
    body: "Restored personal photographs, woven directly into the narrative where the story calls for them, rather than exiled to a gallery at the back. The looking and the telling stay together.",
  },
  {
    title: "The Heritage Run",
    body: "A limited run of sewn, hardcover books, bound to museum standard and built for generations of handling. The number of copies is tied to the milestone the work marks, one volume for each year of a life, for the people chosen to receive them.",
  },
  {
    title: "The Record",
    body: "A press-ready digital master remains with the family permanently, so the volume can be reprinted, at cost, for as long as the lineage wishes to hold it.",
  },
];

const TheWork = () => {
  return (
    <div data-testid="the-work-page">
      <Seo
        title="What Is Delivered · The Raven's Quill"
        description="Not a digital file but an heirloom: a limited heritage run of sewn, museum-standard volumes, with a press-ready master held by the family permanently."
        path="/the-work"
      />
      <section className="pt-20 pb-16 md:pt-28 md:pb-20">
        <Column>
          <Signature label="The Work" testid="the-work-signature" />
          <FadeIn>
            <h1 className="text-[32px] md:text-[38px] italic leading-[1.25] text-[#1C1A17]" data-testid="the-work-headline">
              What Is Delivered
            </h1>
          </FadeIn>
          <FadeIn as="p" className="mt-10 text-[17px] md:text-[18px] leading-[1.8] text-[#1C1A17]">
            The Raven&rsquo;s Quill does not deliver a digital file. A Life Study
            delivers an heirloom, made to outlive everyone who first holds it.
          </FadeIn>
        </Column>
      </section>

      <section className="pb-12">
        <Column>
          <div className="space-y-14 md:space-y-16">
            {DELIVERABLES.map((d) => (
              <FadeIn key={d.title} data-testid={`deliverable-${d.title.toLowerCase().replace(/\s+/g, "-")}`}>
                <h2 className="text-[22px] md:text-[24px] italic text-[#1C1A17]">{d.title}</h2>
                <p className="mt-5 text-[17px] md:text-[18px] leading-[1.8] text-[#1C1A17]">
                  {d.body}
                </p>
              </FadeIn>
            ))}
          </div>
        </Column>
      </section>

      {/* PULL-QUOTE */}
      <section className="py-16 md:py-24">
        <Column>
          <FadeIn>
            <hr className="mx-auto border-0" style={{ width: "72px", height: "0.75px", backgroundColor: "#7A2E2E" }} />
            <p className="mt-10 text-center text-[19px] md:text-[21px] italic leading-[1.6] text-[#1C1A17]" data-testid="the-work-pullquote">
              We do not preserve legacies to be stored. We preserve them to be entered.
            </p>
          </FadeIn>
        </Column>
      </section>

      {/* BEYOND THE BOOK */}
      <section className="py-16 md:py-24">
        <Column>
          <Signature label="Beyond the Book" testid="beyond-signature" />
          <div className="space-y-7 text-[17px] md:text-[18px] leading-[1.8] text-[#1C1A17]">
            <FadeIn as="p">
              For lives whose significance reaches beyond the page, a commission can be
              extended into a fuller preservation of the same fieldwork: a
              broadcast-grade documentary drawn from the residency, a sonic archive of
              the voice and its rooms, and a secured digital vault that holds the entire
              research corpus for the generations to come.
            </FadeIn>
            <FadeIn as="p">
              The heritage run is delivered on a date of your choosing; the occasion is
              yours to shape.
            </FadeIn>
            <FadeIn as="p">These are discussed in consultation, and shaped to the life.</FadeIn>
          </div>
        </Column>
      </section>

      {/* ON PERMANENCE (the site's single reversed band) */}
      <section className="py-24 md:py-32 bg-[#1C1A17]" data-testid="on-permanence-band">
        <Column>
          <Signature label="On Permanence" reversed testid="on-permanence-signature" />
          <div className="space-y-7 text-[17px] md:text-[18px] leading-[1.8] text-[#F4F1EA]">
            <FadeIn as="p">
              Everything now tends toward the digital: weightless, instant, endlessly
              copied. A life can be reduced to a file and, in being reduced, quietly
              lost.
            </FadeIn>
            <FadeIn as="p">
              The Raven&rsquo;s Quill chooses the book on purpose. An object that must be
              carried, opened, and passed from hand to hand transmits more than
              information. It transmits the fact that a life was gathered, weighed, and
              held to matter. The permanence is the point. A bound volume can sit on a
              shelf for a hundred years.
            </FadeIn>
            <FadeIn as="p">
              That is the inheritance: not the data of a life, but its weight, made to
              last.
            </FadeIn>
          </div>

          <FadeIn>
            <p className="mt-14 text-center text-[22px] md:text-[26px] italic leading-[1.6] text-[#F4F1EA]" data-testid="on-permanence-closing">
              We keep a life reachable, so that someone not yet born can one day open the
              book and meet the person inside.
            </p>
          </FadeIn>

          <FadeIn className="mt-14 flex justify-center">
            <OutlineLink to="/commission" reversed testid="the-work-cta">
              Enquire About a Commission
            </OutlineLink>
          </FadeIn>
        </Column>
      </section>
    </div>
  );
};

export default TheWork;
